import { useState, useEffect, useCallback } from "react";
import { getRequest, postRequest, deleteRequest, showMessage } from '../../../../config';
import debounce from 'lodash.debounce';

export const useModules = () => {
    const [moduleList, setModuleList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalModules, setTotalModules] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [statusFilter, setStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [isLoading, setIsLoading] = useState(false);

    const getModules = useCallback(async (page = currentPage, limit = pageSize, search = searchQuery, sort = sortField, order = sortOrder) => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                sortField: sort,
                sortOrder: order
            });

            if (search && search.trim()) {
                params.append('search', search.trim());
            }

            // Add filters
            if (statusFilter !== 'all') {
                params.append('isActive', statusFilter === 'active' ? 'true' : 'false');
            }

            if (typeFilter !== 'all') {
                params.append('type', typeFilter);
            }

            let response = await getRequest(`/modules/temp-modules?${params.toString()}`);
            if (response && response['code'] === 200) {
                const body = response['body'] || {};
                setModuleList(body.modules || []);
                const pagination = body.pagination || {};
                setTotalModules(pagination.total || 0);
                setTotalPages(pagination.totalPages || 0);
                setCurrentPage(pagination.page || 1);
            } else {
                setModuleList([]);
                setTotalModules(0);
                setTotalPages(0);
            }
        } catch (error) {
            console.error('Error fetching modules:', error);
            showMessage('Failed to load modules');
            setModuleList([]);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, pageSize, searchQuery, sortField, sortOrder, statusFilter, typeFilter]);

    useEffect(() => {
        getModules(currentPage, pageSize, searchQuery, sortField, sortOrder);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, searchQuery, sortField, sortOrder, statusFilter, categoryFilter, typeFilter]);

    // Debounced search function
    const debouncedSearch = useCallback(
        debounce((query) => {
            setSearchQuery(query);
            setCurrentPage(1);
        }, 500),
        []
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        debouncedSearch(value);
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('desc');
        }
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handlePageSizeChange = (e) => {
        setPageSize(parseInt(e.target.value));
        setCurrentPage(1);
    };

    const handleStatusFilterChange = (e) => {
        setStatusFilter(e.target.value);
        setCurrentPage(1);
    };

    const handleCategoryFilterChange = (e) => {
        setCategoryFilter(e.target.value);
        setCurrentPage(1);
    };

    const handleTypeFilterChange = (e) => {
        setTypeFilter(e.target.value);
        setCurrentPage(1);
    };

    // Filter modules client-side for status, category, type
    const filteredModules = moduleList.filter(module => {
        if (statusFilter !== 'all' && ((statusFilter === 'active' && !module.isActive) || (statusFilter === 'inactive' && module.isActive))) {
            return false;
        }
        if (categoryFilter !== 'all' && module.category !== categoryFilter) {
            return false;
        }
        if (typeFilter !== 'all' && module.type !== typeFilter) {
            return false;
        }
        return true;
    });

    const activeModule = async (moduleId, moduleName, isActive) => {
        try {
            // Use the new temp-modules toggle-status endpoint
            let res = await postRequest(`/modules/temp-modules/toggle-status`, { moduleId, isActive });
            if (res?.code === 200) {
                showMessage(res['message'] || `Module ${isActive ? 'activated' : 'deactivated'} successfully`);
                await getModules(currentPage, pageSize, searchQuery, sortField, sortOrder);
            } else {
                showMessage(res?.message || 'Failed to update module status');
            }
        } catch (error) {
            console.error('Error updating module status:', error);
            showMessage('Failed to update module status');
        }
    };

    const deleteModule = async (moduleId) => {
        try {
            // Use the new temp-modules delete endpoint with DELETE method
            let res = await deleteRequest(`/modules/temp-modules/${moduleId}`);
            if (res?.code === 200) {
                showMessage(res['message'] || 'Module deleted successfully');
                await getModules(currentPage, pageSize, searchQuery, sortField, sortOrder);
            } else {
                showMessage(res?.message || 'Failed to delete module');
            }
        } catch (error) {
            console.error('Error deleting module:', error);
            showMessage('Failed to delete module');
        }
    };

    return {
        moduleList: filteredModules,
        totalModules,
        totalPages,
        currentPage,
        pageSize,
        isLoading,
        sortField,
        sortOrder,
        searchQuery,
        statusFilter,
        categoryFilter,
        typeFilter,
        handleSearchChange,
        handleSort,
        handlePageChange,
        handlePageSizeChange,
        handleStatusFilterChange,
        handleCategoryFilterChange,
        handleTypeFilterChange,
        activeModule,
        deleteModule,
        getModules
    };
};

